import { useState } from "react";
import { DEVICES_SIZES_IN_PX } from "../../lib/common/constants";
import { useOnboardingProcess } from "../../lib/common/onboarding_steps";
import { useRouteNavigation } from "../../lib/common/routes";
import { TOnboardingProgressStatus, TOnboardingMasterStep, TOnboardingSteps } from "../../lib/types/onboarding_types";
import { TUserTypes } from "../../lib/types/users";
import { evaluators, useWindowSizeMatch } from "../../lib/window-size-matcher";
import { PhoneVerifier } from "../phone-verifier/phone-verifier";
import { useServiceContext } from "../../contexts/service";
import { EmailVerifier } from "../email-verifier/email-verifier";
import { Modal } from "./modal";

type OnboardingProcessProps = {
    progress?: TOnboardingProgressStatus;
    nextStep: TOnboardingMasterStep | null;
    remainingSteps: TOnboardingMasterStep[];
    userType: TUserTypes;
};

type TOnboardingVerificationModalsProps = {
    isPhoneVerified: boolean;
    showEmailModal: boolean;
    showPhoneModal: boolean;
    closeEmailModal: () => void;
    closePhoneModal: () => void;
};

export const useOnboardingStepNavigation = (userType: TUserTypes) => {
    const { getRoute } = useRouteNavigation();
    const { step_candidate_labels, step_company_labels, company_special_steps_routes } = useOnboardingProcess();
    const { security } = useServiceContext();
    const isPhoneVerified = !security.userPhoneNeedsVerification();

    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showPhoneModal, setShowPhoneModal] = useState(false);

    const isCandidate = userType === "tech";

    const stepLabels = isCandidate ? step_candidate_labels : step_company_labels;
    const defaultRoute = isCandidate ? getRoute("dentalTechAccount") : getRoute("dentalLabAccount");

    const getStepHref = (stepName: TOnboardingSteps, anchorId: string) => {
        if (!isCandidate && stepName in company_special_steps_routes) {
            return company_special_steps_routes[stepName as keyof typeof company_special_steps_routes];
        }

        return `${defaultRoute}#${anchorId}`;
    };

    const handleStepClick = (e: React.MouseEvent, stepName: TOnboardingSteps) => {
        if (stepName === "verify_email") {
            e.preventDefault();
            setShowEmailModal(true);
            return;
        }

        if (stepName === "verify_phone") {
            e.preventDefault();
            setShowPhoneModal(true);
        }
    };

    return {
        stepLabels,
        isPhoneVerified,
        showEmailModal,
        showPhoneModal,
        getStepHref,
        handleStepClick,
        closeEmailModal: () => setShowEmailModal(false),
        closePhoneModal: () => setShowPhoneModal(false),
    };
};

export const OnboardingProgressCircle = ({ progressPercentage }: { progressPercentage: number }) => {
    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
    });
    const size = isSmallDevice.fulfilled ? 52 : 80;
    const strokeWidth = 5;
    const halfSize = size / 2;
    const radius = halfSize - strokeWidth;
    const circumference = 2 * Math.PI * radius;
    const dashoffset = circumference * (1 - progressPercentage / 100);

    return (
        <div className="position-relative onboarding-progress-circle">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={halfSize} cy={halfSize} r={radius} stroke="rgba(255,255,255,0.4)" strokeWidth={strokeWidth} fill="none" />
                <circle
                    cx={halfSize}
                    cy={halfSize}
                    r={radius}
                    stroke="#0db1ff"
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashoffset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${halfSize} ${halfSize})`}
                    className="onboarding-progress-circle__progress"
                />
            </svg>
            <div className="position-absolute top-50 start-50 translate-middle fw-semibold onboarding-progress-circle__label">{progressPercentage}%</div>
        </div>
    );
};

export const OnboardingVerificationModals = ({
    isPhoneVerified,
    showEmailModal,
    showPhoneModal,
    closeEmailModal,
    closePhoneModal,
}: TOnboardingVerificationModalsProps) => (
    <>
        {showEmailModal && (
            <Modal onCloseHandler={closeEmailModal} title="Verify Email">
                <div className="modal-body bg-dark">
                    <EmailVerifier onVerified={closeEmailModal} />
                </div>
            </Modal>
        )}

        {showPhoneModal && (
            <Modal onCloseHandler={closePhoneModal} title="Verify your phone">
                <div className="modal-body text-center bg-dark">
                    {!isPhoneVerified && (
                        <p className="p-2">
                            <PhoneVerifier />
                        </p>
                    )}
                </div>
            </Modal>
        )}
    </>
);

export const OnboardingProcess = ({ progress, nextStep, remainingSteps, userType }: OnboardingProcessProps) => {
    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
    });
    const [isOnboardingCollapsed, setIsOnboardingCollapsed] = useState(false);
    const { stepLabels, isPhoneVerified, showEmailModal, showPhoneModal, getStepHref, handleStepClick, closeEmailModal, closePhoneModal } =
        useOnboardingStepNavigation(userType);

    const progressPercentage = progress?.progress ?? 0;

    const renderStepButton = (step: TOnboardingMasterStep, isNextStep: boolean) => {
        const stepName = step.step_name;
        const label = stepLabels[stepName as keyof typeof stepLabels];
        const anchorId = `${stepName}-progress`;
        const href = getStepHref(stepName, anchorId);
        const classes = `py-1 px-2 mb-1 btn border-0 ${isNextStep ? "btn-info text-white" : "btn-outline-light text-muted"}`;

        return (
            <a key={step.step_name} href={href} onClick={(e) => handleStepClick(e, stepName)} className={classes} style={{ fontSize: "0.85rem" }}>
                {label}
            </a>
        );
    };

    const isScreenLarge = !isSmallDevice.fulfilled;
    const toggleIconClassName = isOnboardingCollapsed ? "fa-solid fa-angle-right" : "fa-solid fa-angle-left";

    return (
        <>
            {isScreenLarge && (
                <div className={`onboarding-process shadow-sm rounded-4 p-2${isOnboardingCollapsed ? " onboarding-process-compact-width" : ""}`}>
                    <div className="d-flex align-items-center gap-3">
                        <OnboardingProgressCircle progressPercentage={progressPercentage} />

                        {/* Desktop expanded */}
                        {!isOnboardingCollapsed && (
                            <div className="flex-fill">
                                <small className="d-block mb-1 text-muted fw-bold">Next steps to finish your onboarding process</small>
                                <div className="mt-2">{remainingSteps.map((step) => renderStepButton(step, nextStep?.step_name === step.step_name))}</div>
                            </div>
                        )}

                        {/* Desktop collapsed */}
                        {isOnboardingCollapsed && nextStep && (
                            <div className="flex-fill">
                                <small className="d-block mb-1 text-muted fw-bold">Next step</small>
                                {renderStepButton(nextStep, true)}
                            </div>
                        )}

                        {/* Toggle */}
                        <button
                            type="button"
                            className="onboarding-process-toggle d-inline-flex align-self-stretch align-items-center justify-content-center btn border-0"
                            onClick={() => setIsOnboardingCollapsed((prev) => !prev)}
                            aria-label={isOnboardingCollapsed ? "Expand onboarding panel" : "Collapse onboarding panel"}
                            aria-expanded={!isOnboardingCollapsed}
                        >
                            <i className={`fa-lg ${toggleIconClassName}`} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            )}

            <OnboardingVerificationModals
                isPhoneVerified={isPhoneVerified}
                showEmailModal={showEmailModal}
                showPhoneModal={showPhoneModal}
                closeEmailModal={closeEmailModal}
                closePhoneModal={closePhoneModal}
            />
        </>
    );
};
